import React, { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useRecoilValue } from 'recoil';
import { isNil } from 'lodash';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { proxySetIdState } from 'venn-state';
import type { CombinePortfoliosOrganizeOptionEnum, Portfolio } from 'venn-api';
import { createAggregatedPortfolioV3, createPortfolioV3 } from 'venn-api';
import { ConditionalOverlay, Input, PortfolioTreeView, SidePanelOverlay } from 'venn-components';
import { Icon, Notifications, NotificationType, ShimmerBlock, TooltipBodyDirection } from 'venn-ui-kit';
import { assertNotNil, logExceptionIntoSentry, PORTFOLIOS_LIBRARY_KEY_PREFIX } from 'venn-utils';
import type { PortfolioInformation } from '../CombinePortfoliosWizard/types';
import { useCombinedPortfolios } from '../CombinePortfoliosWizard/logic/useCombinedPortfolios';
import { StepHeader } from '../CombinePortfoliosWizard/components/StepHeader';
import { cn } from '@/lib/utils';

type SaveMode = 'LIVE' | 'SNAPSHOT';

interface CreateAggregatePortfolioSettings {
  combinedPortfolioName: string;
  saveMode: SaveMode;
  organizeOption: CombinePortfoliosOrganizeOptionEnum | null;
  includeHousehold: boolean;
  includeClient: boolean;
}

const ORGANIZE_OPTIONS: { label: string; value: CombinePortfoliosOrganizeOptionEnum }[] = [
  { label: 'By Portfolio', value: 'PORTFOLIO' },
  { label: 'By Asset Class', value: 'ASSET_CLASS' },
];

interface CreateAggregatePortfolioPanelProps {
  onClose: () => void;
  portfolioInformation: PortfolioInformation[];
}

export const CreateAggregatePortfolioPanel = ({
  onClose,
  portfolioInformation,
}: CreateAggregatePortfolioPanelProps) => {
  const proxySetId = useRecoilValue(proxySetIdState);
  const queryClient = useQueryClient();
  const [isSaving, setIsSaving] = useState(false);
  const [settings, setSettings] = useState<CreateAggregatePortfolioSettings>({
    combinedPortfolioName: 'New portfolio',
    saveMode: 'LIVE',
    organizeOption: 'PORTFOLIO',
    includeHousehold: false,
    includeClient: false,
  });

  const { organizeOption, includeHousehold, includeClient, combinedPortfolioName, saveMode } = settings;

  const { portfolios, portfoliosLoading } = useCombinedPortfolios(portfolioInformation, {
    structureOption: 'COMBINED',
    organizeOption,
    combinedPortfolioName,
    includeHousehold,
    includeClient,
  });

  const nameError = combinedPortfolioName.trim().length === 0 ? 'Portfolio name cannot be empty' : null;

  const onCreate = async () => {
    setIsSaving(true);
    try {
      const newPortfolio = await (saveMode === 'LIVE'
        ? createAggregatedPortfolioV3(proxySetId, {
            name: combinedPortfolioName,
            aggregatedPortfolioSettings: {
              combineSettings: {
                structureOption: 'COMBINED',
                organizeOption: organizeOption ?? undefined,
                combinedPortfolioName,
                includeHousehold,
                includeClient,
              },
              portfolioInformation: portfolioInformation.map(({ id, householdName, clientName }) => ({
                id,
                householdName: householdName ?? undefined,
                clientName: clientName ?? undefined,
              })),
            },
          }).then((res) => res.content)
        : createPortfolioV3(proxySetId, {
            ...assertNotNil(portfolios?.[0]),
            id: -1,
            name: combinedPortfolioName,
            draft: false,
            demo: false,
            master: false,
          }).then((res) => res.content));

      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === PORTFOLIOS_LIBRARY_KEY_PREFIX,
      });
      Notifications.notify(`Successfully created ${newPortfolio.name}`, NotificationType.SUCCESS);
      onClose();
    } catch (error) {
      // @ts-expect-error fixme: ts errors from upgrade to typescript 5.5.3
      logExceptionIntoSentry(await error);
      Notifications.notify('An error occurred. Please try again.', NotificationType.ERROR);
      setIsSaving(false);
    }
  };

  return (
    <SidePanelOverlay.Root
      isOpen
      side="right"
      handleClose={onClose}
      hideTopBackButton
      noPadding
      margin={0}
      width={1062}
    >
      <ConditionalOverlay
        contentClassName={cn('flex flex-col flex-1')}
        className={cn('flex flex-col flex-1')}
        center
        condition={isSaving}
      >
        <SidePanelOverlay.Header.Container paddingTop="default">
          <SidePanelOverlay.Header.Main title="Create Aggregate Portfolio" />
        </SidePanelOverlay.Header.Container>
        <div className={cn('flex flex-1')}>
          <div className={cn('flex-1')}>
            <TypeSection settings={settings} setSettings={setSettings} nameError={nameError} />
            <div className={cn('border-0 border-b border-solid border-venn-grey')} />
            <OrganizeSection settings={settings} setSettings={setSettings} />
            <div className={cn('border-0 border-b border-solid border-venn-grey')} />
            <OptionsSection settings={settings} setSettings={setSettings} />
          </div>
          <div className={cn('flex-1')}>
            <PortfolioPreview portfolios={portfolios} portfoliosLoading={portfoliosLoading} settings={settings} />
          </div>
        </div>
        <SidePanelOverlay.Footer
          noMargin
          primaryLabel="CREATE PORTFOLIO"
          primaryDisabled={!!nameError || portfoliosLoading}
          primaryTooltip={nameError ?? undefined}
          tooltipBodyDirection={TooltipBodyDirection.Left}
          onPrimaryClick={onCreate}
          cancelLabel="CANCEL"
          onCancel={onClose}
        />
      </ConditionalOverlay>
    </SidePanelOverlay.Root>
  );
};

interface SectionProps {
  settings: CreateAggregatePortfolioSettings;
  setSettings: React.Dispatch<React.SetStateAction<CreateAggregatePortfolioSettings>>;
}

const TypeSection = ({ settings, setSettings, nameError }: SectionProps & { nameError: string | null }) => (
  <div className={cn('flex flex-col py-[24px] px-[40px] gap-[16px]')}>
    <StepHeader step={1}>Type</StepHeader>
    <Input
      value={settings.combinedPortfolioName}
      onChange={(value) => setSettings((current) => ({ ...current, combinedPortfolioName: value }))}
      refreshedStyling
      autoFocus
      error={!!nameError}
      placeholder="Portfolio name"
    />
    <RadioGroup
      value={settings.saveMode}
      onValueChange={(value: SaveMode) => setSettings((current) => ({ ...current, saveMode: value }))}
      className={cn('gap-[16px]')}
    >
      {(
        [
          { value: 'LIVE', label: 'Live (read-only)', description: 'Remains current as constituent portfolios update' },
          {
            value: 'SNAPSHOT',
            label: 'Snapshot (editable)',
            description: 'Point-in-time snapshot of the constituent portfolios',
          },
        ] as const
      ).map(({ value, label, description }) => (
        <div key={value} className={cn('flex gap-[8px]')}>
          <RadioGroupItem value={value} id={value} state={value === settings.saveMode ? 'checked' : 'unchecked'} />
          <div className={cn('flex flex-col gap-[4px]')}>
            <label htmlFor={value} className={cn('text-[14px] cursor-pointer font-normal')}>
              {label}
            </label>
            <span className={cn('text-[12px] text-venn-grey-60')}>{description}</span>
          </div>
        </div>
      ))}
    </RadioGroup>
  </div>
);

const isIncludeHouseholdEnabled = (organizeOption: CombinePortfoliosOrganizeOptionEnum | null) =>
  !!organizeOption && organizeOption !== 'ASSET_CLASS';
const isIncludeClientEnabled = isIncludeHouseholdEnabled;

const OrganizeSection = ({ settings, setSettings }: SectionProps) => (
  <div className={cn('flex flex-col gap-[16px] py-[24px] px-[40px]')}>
    <StepHeader step={2}>Organize</StepHeader>
    <RadioGroup
      value={settings.organizeOption ?? undefined}
      onValueChange={(value: CombinePortfoliosOrganizeOptionEnum) =>
        setSettings((current) => ({
          ...current,
          organizeOption: value,
          includeHousehold: isIncludeHouseholdEnabled(value) ? current.includeHousehold : false,
          includeClient: isIncludeClientEnabled(value) ? current.includeClient : false,
        }))
      }
      className={cn('gap-[16px]')}
    >
      {ORGANIZE_OPTIONS.map(({ label, value }) => (
        <div key={value} className={cn('flex gap-[8px]')}>
          <RadioGroupItem
            value={value}
            id={value}
            state={value === settings.organizeOption ? 'checked' : 'unchecked'}
          />
          <label htmlFor={value} className={cn('text-[14px] cursor-pointer font-normal')}>
            {label}
          </label>
        </div>
      ))}
    </RadioGroup>
  </div>
);

const OptionsSection = ({ settings, setSettings }: SectionProps) => {
  const householdEnabled = isIncludeHouseholdEnabled(settings.organizeOption);
  const clientEnabled = isIncludeClientEnabled(settings.organizeOption);

  return (
    <div className={cn('flex flex-col gap-[16px] py-[24px] px-[40px]')}>
      <StepHeader step={3} disabled={!householdEnabled && !clientEnabled}>
        Options
      </StepHeader>
      {householdEnabled && (
        <div className={cn('flex gap-[8px] items-center')}>
          <Checkbox
            id="include-household"
            state={settings.includeHousehold ? 'checked' : 'unchecked'}
            onCheckedChange={(value: boolean) => setSettings((current) => ({ ...current, includeHousehold: value }))}
          />
          <label htmlFor="include-household" className={cn('text-[14px] cursor-pointer font-normal')}>
            Include Household
          </label>
        </div>
      )}
      {clientEnabled && (
        <div className={cn('flex gap-[8px] items-center')}>
          <Checkbox
            id="include-client"
            state={settings.includeClient ? 'checked' : 'unchecked'}
            onCheckedChange={(value: boolean) => setSettings((current) => ({ ...current, includeClient: value }))}
          />
          <label htmlFor="include-client" className={cn('text-[14px] cursor-pointer font-normal')}>
            Include Client
          </label>
        </div>
      )}
    </div>
  );
};

const getDisplayDepth = (settings: CreateAggregatePortfolioSettings) =>
  1 + (settings.includeHousehold ? 1 : 0) + (settings.includeClient ? 1 : 0);

const PortfolioPreview = ({
  portfolios,
  portfoliosLoading,
  settings,
}: {
  portfolios: Portfolio[] | null;
  portfoliosLoading: boolean;
  settings: CreateAggregatePortfolioSettings;
}) => (
  <div className={cn('w-full h-full flex flex-col py-[24px] px-[40px] bg-venn-grey-10')}>
    <div className={cn('text-[24px]')}>Preview</div>
    <div className={cn('border-0 border-b border-solid border-venn-grey my-[16px]')} />
    {portfoliosLoading || isNil(portfolios) ? (
      <ShimmerBlock />
    ) : (
      <div className={cn('flex-1 basis-0 overflow-y-auto')}>
        {portfolios.map((portfolio) => (
          <div key={portfolio.id ?? portfolio.name} className={cn('py-[4px]')}>
            <PortfolioTreeView portfolio={portfolio} depth={getDisplayDepth(settings)} />
          </div>
        ))}
      </div>
    )}
    {portfolios?.length === 0 && (
      <div className={cn('flex flex-1 items-center justify-center')}>
        <Icon type="folders" className={cn('text-[187px] text-venn-grey-20')} />
      </div>
    )}
  </div>
);
